import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
    xs: {
        padding: `0 ${tokens.globals["spacing-sm"]}`,
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-7"],
    },
    sm: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["md-12px"],
        lineHeight: "16px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-8"],
    },
    md: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-9"],
    },
    lg: {
        padding: `0 ${tokens.globals["spacing-2-5"]}`,
        fontSize: tokens.typography.fontSize["lg-14px"],
        lineHeight: "20px",
        borderRadius: tokens.globals["border-radius"],
        height: tokens.globals["spacing-10"],
    },
};

const StyledInputGroup = styled.div({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-px"],
});

const StyledTop = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
    minHeight: tokens.globals["spacing-5"],
});

const StyledLabel = styled.label({
    margin: 0,
    flex: "1 0 0",
    minWidth: 0,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
    lineHeight: "20px",
});

const StyledRequired = styled.span({
    color: tokens.colors.ui.destructive,
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
    marginLeft: tokens.globals["spacing-0-5"],
    position: "relative",
    top: tokens.globals["spacing-0-5"],
});

const StyledInfoIcon = styled.i({
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    cursor: "help",
});

const StyledInput = styled.div(({ $size = "lg", $disabled = false, $error = false, $multiline = false }) => {
    const config = sizes[$size] || sizes.lg;

    return {
        width: "100%",
        height: $multiline ? "auto" : config.height,
        minHeight: config.height,
        padding: $multiline ? `${tokens.globals["spacing-2"]} ${tokens.globals["spacing-2-5"]}` : config.padding,
        backgroundColor: $disabled ? tokens.colors.neutral.grey["100"] : tokens.colors.surfaces["container-background"],
        border: `${tokens.globals["spacing-px"]} solid ${$error ? tokens.colors.ui.destructive : tokens.colors.surfaces["border-divider"]}`,
        borderRadius: config.borderRadius,
        boxShadow: "none",
        display: "flex",
        alignItems: $multiline ? "flex-start" : "center",
        gap: tokens.globals["spacing-sm"],
        opacity: $disabled ? 0.6 : 1,
        cursor: $disabled ? "not-allowed" : "text",
        transition: "border-color 0.15s ease-in-out",

        "&:hover": $disabled ? {} : {
            borderColor: $error ? tokens.colors.ui.destructive : tokens.colors.neutral.grey["500"],
        },

        "&:focus-within": $disabled ? {} : {
            borderColor: $error ? tokens.colors.ui.destructive : tokens.colors.ui.ring,
        },
    };
});

const StyledInputField = styled.input(({ $size = "lg", $multiline = false, disabled = false }) => {
    const config = sizes[$size] || sizes.lg;

    return {
        flex: 1,
        minWidth: 0,
        width: "100%",
        padding: 0,
        backgroundColor: "transparent",
        border: "none",
        outline: "none",
        resize: $multiline ? "vertical" : "none",
        color: disabled ? tokens.colors.text.disabled : tokens.colors.text.primary,
        fontFamily: tokens.typography.fontFamilies["albert-sans"],
        fontSize: config.fontSize,
        fontWeight: tokens.typography.fontWeights.regular,
        lineHeight: config.lineHeight,
        cursor: disabled ? "not-allowed" : "text",
        ...(!$multiline && {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        }),

        "&::placeholder": {
            color: tokens.colors.text.secondary,
            fontSize: config.fontSize,
            fontWeight: tokens.typography.fontWeights.regular,
        },
    };
});

const StyledPrefix = styled.span({
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.regular,
});

const StyledPrefixDivider = styled.span({
    alignSelf: "stretch",
    flexShrink: 0,
    width: tokens.globals["spacing-px"],
    background: tokens.colors.surfaces["border-divider"],
});

const StyledRightIcon = styled.span({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["base-16px"],
    lineHeight: 1,
});

const StyledMessage = styled.span(({ $error = false }) => ({
    color: $error ? tokens.colors.ui.destructive : tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["md-12px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: $error ? tokens.typography.fontWeights.medium : tokens.typography.fontWeights.regular,
    lineHeight: "16px",
}));

/**
 * Standard AIE text input, keeping label/error/helper/disabled states consistent with SelectDropdown.
 * @param {string|number} [props.value] Controlled value.
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} [props.onChange] Change handler.
 * @param {string} [props.type] Native input type (e.g. "text", "email", "number").
 * @param {boolean} [props.multiline] Renders a resizable `<textarea>` instead of an `<input>`.
 * @param {React.Ref} ref Forwarded to the native input/textarea element.
 * @param {React.ReactNode} [props.prefix] Unit or currency adornment (e.g. "R") rendered
 *   inside the field's own border, divided from the value by a hairline.
 * Also: placeholder, size, disabled, disabledReason, error, label, required, info, helperText, errorText, rows, iconRight, title, id, name, className, onBlur, onFocus (self-explanatory).
 */
const TextInput = forwardRef(
    function TextInput(
        {
            placeholder = "Type here",
            value,
            onChange,
            size = "lg",
            disabled = false,
            disabledReason,
            error = false,
            label,
            required = false,
            info,
            helperText,
            errorText,
            type = "text",
            multiline = false,
            rows,
            iconRight,
            prefix,
            title,
            id,
            name,
            className,
            onBlur,
            onFocus,
            ...props
        },
        ref
    ) {
        const inputId = id || name || undefined;
        const messageId = inputId ? `${inputId}-message` : undefined;
        const showError = Boolean(error || errorText);
        const message = errorText || helperText;

        return (
            <StyledInputGroup className={className} title={title || (disabled ? disabledReason : undefined)}>
                {label && (
                    <StyledTop>
                        <StyledLabel htmlFor={inputId}>
                            {label}
                            {required && <StyledRequired aria-hidden="true">*</StyledRequired>}
                        </StyledLabel>
                        {info && (
                            <StyledInfoIcon
                                className="fal fa-info-circle"
                                title={info}
                                aria-label={info}
                            />
                        )}
                    </StyledTop>
                )}

                <StyledInput $size={size} $disabled={disabled} $error={showError} $multiline={multiline}>
                    {prefix && (
                        <>
                            <StyledPrefix>{prefix}</StyledPrefix>
                            <StyledPrefixDivider aria-hidden="true" />
                        </>
                    )}

                    <StyledInputField
                        {...props}
                        as={multiline ? "textarea" : "input"}
                        rows={multiline ? rows : undefined}
                        ref={ref}
                        id={inputId}
                        name={name}
                        type={multiline ? undefined : type}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        disabled={disabled}
                        required={required}
                        placeholder={placeholder}
                        $size={size}
                        $multiline={multiline}
                        aria-invalid={showError || undefined}
                        aria-required={required || undefined}
                        aria-describedby={message ? messageId : undefined}
                    />

                    {iconRight && <StyledRightIcon aria-hidden="true">{iconRight}</StyledRightIcon>}
                </StyledInput>

                {message && (
                    <StyledMessage id={messageId} $error={Boolean(errorText)} role={errorText ? "alert" : undefined}>
                        {message}
                    </StyledMessage>
                )}
            </StyledInputGroup>
        );
    }
);

TextInput.displayName = "TextInput";

export default TextInput;
